import { useEffect } from "react";
import type { StationV2 } from "@orvis/core";

/**
 * Wire browser Media Session (lockscreen / notification-shade controls)
 * to whatever the app is currently playing.
 *
 * Works on:
 *  - Chrome / Edge / Safari desktop → mini media control in the browser
 *  - iOS Safari + Capacitor WebView → lockscreen controls (needs
 *    UIBackgroundModes=audio in Info.plist, which apps/mobile-* already set)
 *  - Android Chrome + Capacitor WebView → notification-shade controls
 *
 * `onPause` / `onResume` receive the caller's own start/stop functions so
 * the lockscreen buttons drive the same state React does. Pass `undefined`
 * for `station` when nothing is playing — that clears the Media Session.
 */
export function useMediaSession(
  station: StationV2 | undefined,
  handlers: {
    paused?: boolean;
    onPause?: () => void;
    onResume?: () => void;
    onStop?: () => void;
    /** Lockscreen / headset next & previous — zaps through the visible list. */
    onNext?: () => void;
    onPrev?: () => void;
    /** Current song/show, when known — becomes the title, with the station as artist. */
    nowPlaying?: string;
  } = {}
): void {
  useEffect(() => {
    if (typeof navigator === "undefined") return;
    const ms = navigator.mediaSession;
    if (!ms) return;

    if (!station) {
      // Nothing playing — clear metadata and drop action handlers.
      ms.metadata = null;
      ms.playbackState = "none";
      ms.setActionHandler("play", null);
      ms.setActionHandler("pause", null);
      ms.setActionHandler("stop", null);
      ms.setActionHandler("nexttrack", null);
      ms.setActionHandler("previoustrack", null);
      return;
    }

    const artwork = station.logo
      ? [{ src: station.logo, sizes: "192x192", type: "image/png" }]
      : [];
    ms.metadata = new MediaMetadata({
      title: handlers.nowPlaying ?? station.name,
      artist: handlers.nowPlaying ? station.name : station.description ?? "Orvis",
      album: (station.country ?? "").toUpperCase() || "Orvis",
      artwork,
    });
    ms.playbackState = handlers.paused ? "paused" : "playing";

    if (handlers.onPause) ms.setActionHandler("pause", handlers.onPause);
    if (handlers.onResume) ms.setActionHandler("play", handlers.onResume);
    if (handlers.onStop) ms.setActionHandler("stop", handlers.onStop);
    // Some engines throw on action names they don't know; next/prev are
    // newer than play/pause/stop.
    try {
      ms.setActionHandler("nexttrack", handlers.onNext ?? null);
      ms.setActionHandler("previoustrack", handlers.onPrev ?? null);
    } catch { /* unsupported */ }

    return () => {
      ms.setActionHandler("play", null);
      ms.setActionHandler("pause", null);
      ms.setActionHandler("stop", null);
      try {
        ms.setActionHandler("nexttrack", null);
        ms.setActionHandler("previoustrack", null);
      } catch { /* unsupported */ }
    };
  }, [station, handlers.paused, handlers.onPause, handlers.onResume, handlers.onStop, handlers.onNext, handlers.onPrev, handlers.nowPlaying]);
}
